import type {QueryKey} from "@tanstack/query-core";
import {
    QueryObserverLoadingErrorResult,
    QueryObserverRefetchErrorResult,
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult
} from "@tanstack/react-query";
import {errorize} from "../bomb/errorize.tsx";

export type InfallibleUseQueryResult<TData, TError>
    = Exclude<UseQueryResult<TData, TError>,
    QueryObserverRefetchErrorResult<TData, TError>
    | QueryObserverLoadingErrorResult<TData, TError>>;

export function useInfallibleQuery<
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
>(inOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): InfallibleUseQueryResult<TData, TError> {
    const tqc = useQueryClient();
    const defaultQo = tqc.getQueryDefaults(inOptions.queryKey);
    const options = inOptions.throwOnError || defaultQo.throwOnError
        ? inOptions
        : {...inOptions, throwOnError: true};
    const rv = useQuery(options);
    if (rv.isError) {
        throw errorize(rv.error);
    }
    return rv;
}
